/* eslint-disable */


import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { sortByName } from "../lib/utils";
import { Plus, Trash2, ArrowDown, ArrowUp, Settings2, Tag, ArrowRightLeft, ScanLine, Warehouse, ChevronRight, ChevronDown, MapPin, Search, X, Download, AlertTriangle, CalendarX, GlassWater, RefreshCw, FileSearch, Snowflake } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { fmtDate, fmtDateTime, fmtNum, movementLabel, dlcStatus, dlcBadgeClass } from "../lib/format";
import { useConfirm } from "../components/ConfirmDialog";
import { downloadBlob } from "../lib/download";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";
import BarcodeScanner from "../components/BarcodeScanner";
import { tryUnmojibakeScan } from "../lib/scan-unmojibake";
import WithdrawLotDialog from "../components/stock/WithdrawLotDialog";
import { FreezeLotDialog } from "../components/stock/FreezeLotDialog";
import VisualTransferDialog from "../components/stock/VisualTransferDialog";
import LotCombobox from "../components/LotCombobox";
import { generateEAN13 } from "../lib/ean13";

const mvTypeLabel = { ...movementLabel, TRANSFER: "Transfert" };

export default function Stocks() {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [lots, setLots] = useState([]);
  const [movements, setMovements] = useState([]);
  const [settings, setSettings] = useState(null);
  const [depotFilter, setDepotFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [dlcFilter, setDlcFilter] = useState(() => {
    const v = (typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("dlc") : null);
    return ["risk", "expired", "soon"].includes(v) ? v : "all";
  });
  // Cache les lots à quantité 0 par défaut. Ils restent en base pour la traçabilité
  // HACCP (mouvements historiques préservés) mais sortent de la vue active.
  const [hideEmptyLots, setHideEmptyLots] = useState(true);

  useEffect(() => {
    const v = searchParams.get("dlc");
    if (v && ["risk", "expired", "soon", "all"].includes(v)) setDlcFilter(v);
    const lot = searchParams.get("lot");
    if (lot) setSearchTerm(lot);
  }, [searchParams]);

  const [lotOpen, setLotOpen] = useState(false);
  const [mvOpen, setMvOpen] = useState(false);
  const [trOpen, setTrOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [dlcOpen, setDlcOpen] = useState(false);
  const [dlcDistId, setDlcDistId] = useState("");
  const [dlcThreshold, setDlcThreshold] = useState(() => new Date().toISOString().slice(0, 10));
  const [dlcPreview, setDlcPreview] = useState(null);
  const [dlcLoading, setDlcLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  const toggleGroup = (key) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const [lotForm, setLotForm] = useState({ product_id: "", lot_number: "", quantity: 0, distributor_id: "", emplacement_number: "", barquette_index: "", dlc: "", production_date: "", notes: "" });
  const [mvForm, setMvForm] = useState({ product_id: "", lot_id: "", type: "IN", quantity: 0, reason: "", distributor_id: "" });
  const [trForm, setTrForm] = useState({ lot_id: "", quantity: 0, to_distributor_id: "", to_emplacement_number: "", to_barquette_index: "", reason: "" });

  // Phase 87bc — Onglet "Boissons" : lots perpétuels (stock_managed=false, dlc=null)
  const [perpetualLots, setPerpetualLots] = useState([]);
  const [perpetualLoaded, setPerpetualLoaded] = useState(false);
  const [refillOpen, setRefillOpen] = useState(false);
  const [refillForm, setRefillForm] = useState({ product_id: "", distributor_id: "", quantity: 0, emplacement_number: "", notes: "" });
  const [refillSaving, setRefillSaving] = useState(false);
  // Phase 67b — Unification : ce dialog couvre maintenant aussi les "mises hors circuit"
  // (Perte DLC, Casse, Bloquée en machine, Retour Conthey). On bascule via `outType`.
  const [outType, setOutType] = useState("TRANSFER"); // "TRANSFER" | "WITHDRAW"
  const [withdrawReason, setWithdrawReason] = useState(""); // DLC_EXPIRED | BROKEN | BLOCKED | RETURN_CONTHEY
  const [withdrawNotes, setWithdrawNotes] = useState("");

  const load = async () => {
    const [p, d, l, m, s] = await Promise.all([
      api.get("/products"),
      api.get("/distributors"),
      api.get("/lots?active_only=true"),
      api.get("/movements?limit=300"),
      api.get("/settings").catch(() => ({ data: null })),
    ]);
    setProducts(p.data); setDistributors(d.data); setLots(l.data); setMovements(m.data);
    if (s?.data) setSettings(s.data);
  };
  useEffect(() => { load(); loadPerpetual(); }, []);
  const confirm = useConfirm();

  // Phase 87bc — Charge les lots perpétuels (boissons)
  const loadPerpetual = async () => {
    try {
      const { data } = await api.get("/lots/perpetual");
      setPerpetualLots(data || []);
      setPerpetualLoaded(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement boissons");
    }
  };

  const openRefill = (presetProductId = "", presetDistributorId = "", presetEmplacement = "") => {
    setRefillForm({
      product_id: presetProductId,
      distributor_id: presetDistributorId,
      quantity: 0,
      emplacement_number: presetEmplacement ? String(presetEmplacement) : "",
      notes: "",
    });
    setRefillOpen(true);
  };

  const saveRefill = async () => {
    if (!refillForm.product_id) { toast.error("Choisissez un produit"); return; }
    if (!refillForm.distributor_id) { toast.error("Choisissez un distributeur"); return; }
    const qty = Number(refillForm.quantity);
    if (!qty || isNaN(qty)) { toast.error("Quantité doit être ≠ 0"); return; }
    setRefillSaving(true);
    try {
      const payload = {
        product_id: refillForm.product_id,
        distributor_id: refillForm.distributor_id,
        quantity: qty,
        notes: refillForm.notes.trim(),
      };
      if (refillForm.emplacement_number) payload.emplacement_number = Number(refillForm.emplacement_number);
      const { data } = await api.post("/lots/refill-perpetual", payload);
      const action = qty > 0 ? `+${qty} ajoutée${qty > 1 ? "s" : ""}` : `${qty} (correction inventaire)`;
      toast.success(`${action} · nouvelle qty : ${data.new_quantity}`);
      setRefillOpen(false);
      await loadPerpetual();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de remplissage");
    } finally {
      setRefillSaving(false);
    }
  };

  const productMap = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);
  const lotMap = useMemo(() => Object.fromEntries(lots.map((l) => [l.id, l])), [lots]);
  const distMap = useMemo(() => Object.fromEntries(distributors.map((d) => [d.id, d])), [distributors]);

  // Calcule la DLC à partir du produit et de la date de production
  const computeDlc = (productId, prodDate) => {
    if (!productId || !prodDate) return "";
    const p = products.find((x) => x.id === productId);
    if (!p || !p.shelf_life_days) return "";
    const d = new Date(prodDate);
    if (isNaN(d.getTime())) return "";
    d.setDate(d.getDate() + Number(p.shelf_life_days));
    return d.toISOString().slice(0, 10);
  };

  const updateLotForm = (patch) => {
    setLotForm((prev) => {
      const next = { ...prev, ...patch };
      // Auto-calcul DLC si le produit ou la date de production vient de changer et que DLC est vide
      if (("product_id" in patch || "production_date" in patch)) {
        const auto = computeDlc(next.product_id, next.production_date);
        if (auto) next.dlc = auto;
      }
      return next;
    });
  };

  const saveLot = async () => {
    if (!lotForm.product_id || !lotForm.lot_number) { toast.error("Produit et n° de lot requis"); return; }
    try {
      await api.post("/lots", {
        ...lotForm,
        quantity: Number(lotForm.quantity) || 0,
        distributor_id: lotForm.distributor_id || null,
        emplacement_number: lotForm.emplacement_number ? Number(lotForm.emplacement_number) : null,
        barquette_index: lotForm.barquette_index ? Number(lotForm.barquette_index) : null,
        dlc: lotForm.dlc || null,
        production_date: lotForm.production_date || null,
      });
      toast.success("Lot créé");
      setLotOpen(false);
      setLotForm({ product_id: "", lot_number: "", quantity: 0, distributor_id: "", emplacement_number: "", barquette_index: "", dlc: "", production_date: "", notes: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const saveMovement = async () => {
    if (!mvForm.product_id || !mvForm.quantity) { toast.error("Produit et quantité requis"); return; }
    await api.post("/movements", {
      ...mvForm,
      quantity: Number(mvForm.quantity),
      lot_id: mvForm.lot_id || null,
      distributor_id: mvForm.distributor_id || null,
    });
    toast.success("Mouvement enregistré");
    setMvOpen(false);
    setMvForm({ product_id: "", lot_id: "", type: "IN", quantity: 0, reason: "", distributor_id: "" });
    await load();
  };

  const dlcRunPreview = async () => {
    if (!dlcDistId) { toast.error("Choisissez un distributeur"); return; }
    setDlcLoading(true);
    try {
      const r = await api.post("/dlc-removal", {
        distributor_id: dlcDistId,
        threshold_date: dlcThreshold,
        dry_run: true,
      });
      setDlcPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur preview");
    } finally {
      setDlcLoading(false);
    }
  };

  const dlcExecute = async () => {
    if (!dlcPreview || dlcPreview.lots_count === 0) return;
    const ok = await confirm({
      title: `Retirer ${dlcPreview.lots_count} lot${dlcPreview.lots_count > 1 ? "s" : ""} ?`,
      description: `${dlcPreview.total_quantity} barquette${dlcPreview.total_quantity > 1 ? "s" : ""} avec DLC ≤ ${dlcThreshold} seront retirées de "${dlcPreview.distributor_name}". Cette action crée des mouvements OUT (perte DLC) et passe les lots concernés à 0. Irréversible.`,
      confirmText: "Retirer définitivement",
      destructive: true,
    });
    if (!ok) return;
    setDlcLoading(true);
    try {
      const r = await api.post("/dlc-removal", {
        distributor_id: dlcDistId,
        threshold_date: dlcThreshold,
        dry_run: false,
      });
      toast.success(`${r.data.lots_removed_count} lot${r.data.lots_removed_count > 1 ? "s retirés" : " retiré"} (${r.data.total_quantity_removed} barquettes)`);
      setDlcOpen(false);
      setDlcPreview(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur retrait DLC");
    } finally {
      setDlcLoading(false);
    }
  };

  const saveTransfer = async () => {
    if (!trForm.lot_id || !trForm.quantity) { toast.error("Lot et quantité requis"); return; }

    // Phase 67b — Mode "Mise hors circuit" : appelle /lots/{id}/withdraw avec motif HACCP
    if (outType === "WITHDRAW") {
      if (!withdrawReason) { toast.error("Motif de mise hors circuit requis"); return; }
      if (withdrawReason === "BROKEN" && !withdrawNotes.trim()) {
        toast.error("Notes obligatoires pour le motif Casse / rebut"); return;
      }
      try {
        await api.post(`/lots/${trForm.lot_id}/withdraw`, {
          reason: withdrawReason,
          quantity: Number(trForm.quantity),
          destination_distributor_id: null,
          notes: withdrawNotes.trim() || trForm.reason || "",
        });
        toast.success("Mise hors circuit enregistrée — historique HACCP préservé");
        setTrOpen(false);
        setTrForm({ lot_id: "", quantity: 0, to_distributor_id: "", to_emplacement_number: "", reason: "" });
        setOutType("TRANSFER"); setWithdrawReason(""); setWithdrawNotes("");
        await load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Erreur lors du retrait");
      }
      return;
    }

    // Mode "Transfert" classique
    if (!trForm.to_distributor_id) { toast.error("Dépôt destination requis"); return; }
    const targetDist = distMap[trForm.to_distributor_id];
    const isClient = targetDist?.type === "CLIENT";
    if (!isClient && !trForm.to_emplacement_number) { toast.error("Emplacement de destination requis"); return; }
    try {
      await api.post("/transfers", {
        lot_id: trForm.lot_id,
        quantity: Number(trForm.quantity),
        to_distributor_id: trForm.to_distributor_id,
        to_emplacement_number: isClient ? null : Number(trForm.to_emplacement_number),
        reason: trForm.reason,
      });
      toast.success("Transfert effectué");
      setTrOpen(false);
      setTrForm({ lot_id: "", quantity: 0, to_distributor_id: "", to_emplacement_number: "", reason: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur transfert");
    }
  };

  // Utilise la même génération que la page Étiquettes (lib/ean13.js)
  const computeEAN13Base = (sku, lotNumber) => generateEAN13(sku, lotNumber);

  const handleScan = async (decoded) => {
    setScanOpen(false);
    // Phase 86e — Filet de sécurité : corrige le mojibake si le scanner est mal configuré
    const fixed = tryUnmojibakeScan(decoded || "");
    if (fixed !== decoded) {
      toast.warning("Payload scanner corrigé (clavier US détecté). Pensez à configurer votre scanner en AZERTY.", { duration: 4000 });
    }
    const trimmed = fixed.trim();
    if (!trimmed) return;

    let product = null;
    let lot = null;

    // 1) Try EAN13: 13 digits, match against generated codes for known products+lots
    if (/^\d{12,13}$/.test(trimmed)) {
      const base = trimmed.length === 13 ? trimmed.slice(0, 12) : trimmed;
      // Search product by EAN13 generated from (sku, "") for product-only labels
      product = products.find((p) => computeEAN13Base(p.sku, "") === base);
      if (!product) {
        // Search lot match (sku, lot_number)
        for (const l of lots) {
          const p = products.find((x) => x.id === l.product_id);
          if (p && computeEAN13Base(p.sku, l.lot_number) === base) {
            product = p;
            lot = l;
            break;
          }
        }
      }
    }

    // 2) Fallback: legacy "SKU-LOT" format
    if (!product) {
      let sku = trimmed;
      let lotNumber = null;
      const match = trimmed.match(/^(.+?)-(L[0-9A-Z-]+)$/i);
      if (match) { sku = match[1]; lotNumber = match[2]; }
      product = products.find((p) => p.sku === sku);
      if (!product) {
        // Try QR JSON payload
        try {
          const obj = JSON.parse(trimmed);
          if (obj.sku) {
            product = products.find((p) => p.sku === obj.sku);
            lotNumber = obj.lot || null;
          }
        } catch {}
      }
      if (!product) {
        try {
          const res = await api.get(`/products/by-sku/${encodeURIComponent(sku)}`);
          product = res.data;
        } catch {
          toast.error(`Code "${trimmed}" — produit introuvable`);
          return;
        }
      }
      if (lotNumber) {
        lot = lots.find((l) => l.product_id === product.id && l.lot_number === lotNumber) || null;
      }
    }

    setMvForm({
      product_id: product.id,
      lot_id: lot?.id || "",
      type: "IN",
      quantity: 1,
      reason: `Scan ${trimmed}`,
      distributor_id: lot?.distributor_id || "",
    });
    setMvOpen(true);
    toast.success(`Produit trouvé: ${product.name}${lot ? ` · Lot ${lot.lot_number}` : ""}`);
  };

  const filteredLots = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return lots.filter((l) => {
      if (hideEmptyLots && (l.quantity || 0) <= 0) return false;
      if (depotFilter !== "all") {
        if (depotFilter === "unassigned") {
          if (l.distributor_id) return false;
        } else if (depotFilter === "type:AUTOMATIQUE" || depotFilter === "type:CLIENT") {
          const wantType = depotFilter.split(":")[1];
          const dist = distMap[l.distributor_id];
          if (!dist || (dist.type || "AUTOMATIQUE") !== wantType) return false;
        } else if (l.distributor_id !== depotFilter) {
          return false;
        }
      }
      if (dlcFilter !== "all") {
        const s = dlcStatus(l.dlc);
        const kind = s?.kind || "none";
        if (dlcFilter === "expired" && kind !== "expired") return false;
        if (dlcFilter === "soon" && kind !== "soon") return false;
        if (dlcFilter === "risk" && !(kind === "expired" || kind === "soon")) return false;
      }
      if (!q) return true;
      const p = productMap[l.product_id];
      const haystack = [
        l.lot_number,
        p?.name,
        p?.sku,
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [lots, depotFilter, distMap, searchTerm, dlcFilter, productMap, hideEmptyLots]);

  // Regroupement des lots par (produit + n° de lot)
  const groupedLots = useMemo(() => {
    const map = new Map();
    for (const l of filteredLots) {
      const key = `${l.product_id}::${l.lot_number}`;
      const existing = map.get(key);
      if (existing) {
        existing.quantity += l.quantity || 0;
        existing.lots.push(l);
        existing.depot_ids.add(l.distributor_id || "_unassigned");
        if (!existing.dlc && l.dlc) existing.dlc = l.dlc;
      } else {
        map.set(key, {
          key,
          product_id: l.product_id,
          lot_number: l.lot_number,
          quantity: l.quantity || 0,
          dlc: l.dlc,
          lots: [l],
          depot_ids: new Set([l.distributor_id || "_unassigned"]),
          first_lot_id: l.id,
        });
      }
    }
    // Tri stable des barquettes au sein de chaque groupe
    for (const g of map.values()) {
      g.lots.sort((a, b) => {
        const da = (a.distributor_id || "");
        const db = (b.distributor_id || "");
        if (da !== db) return da.localeCompare(db);
        const ea = a.emplacement_number ?? Number.MAX_SAFE_INTEGER;
        const eb = b.emplacement_number ?? Number.MAX_SAFE_INTEGER;
        if (ea !== eb) return ea - eb;
        return (a.barquette_index ?? 0) - (b.barquette_index ?? 0);
      });
    }
    return Array.from(map.values());
  }, [filteredLots]);

  // Phase 63 — anciennes fonctions deleteGroup/deleteSingleLot supprimées :
  // remplacées par <WithdrawLotDialog> (motif explicite + traçabilité HACCP).
  // Le bouton 🗑️ ouvre désormais le dialog "Retirer du stock" au lieu d'effacer
  // dans le vide (DELETE /lots/{id} renvoie maintenant 400 sans force=true).

  const expireLot = async (lot, isGroup = false) => {
    const p = productMap[lot.product_id];
    const ok = await confirm({
      title: "Marquer comme périmé (DLC dépassée) ?",
      description: `Toute la quantité disponible (${fmtNum(lot.quantity, p?.unit)}) du lot ${lot.lot_number} sera marquée en perte. Un mouvement de sortie « DLC dépassée » sera créé pour la traçabilité.`,
      confirmText: "Confirmer la perte",
      cancelText: "Annuler",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.post(`/lots/${lot.id}/expire`, {});
      toast.success(`Lot marqué périmé · perte enregistrée`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const expireGroup = async (g) => {
    const p = productMap[g.product_id];
    const totalQty = g.lots.reduce((acc, x) => acc + (x.quantity || 0), 0);
    const ok = await confirm({
      title: `Marquer ${g.lots.length} barquette${g.lots.length > 1 ? "s" : ""} comme périmée${g.lots.length > 1 ? "s" : ""} ?`,
      description: `Toute la quantité (${fmtNum(totalQty, p?.unit)}) du lot ${g.lot_number} sera marquée en perte. Un mouvement « DLC dépassée » sera créé pour chaque barquette.`,
      confirmText: "Confirmer toutes les pertes",
      cancelText: "Annuler",
      destructive: true,
    });
    if (!ok) return;
    try {
      for (const l of g.lots) {
        if ((l.quantity || 0) > 0) {
          await api.post(`/lots/${l.id}/expire`, {});
        }
      }
      toast.success(`${g.lots.length} barquette${g.lots.length > 1 ? "s" : ""} marquée${g.lots.length > 1 ? "s" : ""} périmée${g.lots.length > 1 ? "s" : ""}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const expandableGroupKeys = useMemo(() => groupedLots.filter((g) => g.lots.length > 1).map((g) => g.key), [groupedLots]);
  const allExpanded = expandableGroupKeys.length > 0 && expandableGroupKeys.every((k) => expandedGroups.has(k));
  const toggleAllGroups = () => {
    setExpandedGroups(allExpanded ? new Set() : new Set(expandableGroupKeys));
  };

  const MvIcon = ({ t }) => t === "IN" ? <ArrowDown className="w-3.5 h-3.5 text-[#2D6A4F]" />
    : t === "OUT" ? <ArrowUp className="w-3.5 h-3.5 text-[#C05C47]" />
    : t === "TRANSFER" ? <ArrowRightLeft className="w-3.5 h-3.5 text-[#3B82F6]" />
    : <Settings2 className="w-3.5 h-3.5 text-[#D4A373]" />;

  const depotFromId = (id) => id ? (distMap[id]?.name || "—") : "—";
  const depotBadge = (id) => {
    if (!id) return <span className="text-xs text-[#A3A6A3]">Non assigné</span>;
    const d = distMap[id];
    return <Badge variant="outline" className="bg-[#EAF3EE] border-[#C5DBCF] text-[#1F4E3D]">{d?.code || d?.name || "—"}</Badge>;
  };

  const renderDlc = (dlc) => {
    if (!dlc) return <span className="text-xs text-[#A3A6A3]">—</span>;
    const s = dlcStatus(dlc);
    if (!s || s.kind === "ok") return <span>{fmtDate(dlc)}</span>;
    const label = s.kind === "expired"
      ? `Dépassée${Math.abs(s.days) > 0 ? ` (J${s.days})` : ""}`
      : `J-${s.days}`;
    return (
      <Badge variant="outline" className={`${dlcBadgeClass(s.kind)} font-medium gap-1.5`} title={fmtDate(dlc)}>
        {fmtDate(dlc)} · {label}
      </Badge>
    );
  };

  const exportCsv = () => {
    if (filteredLots.length === 0) {
      toast.error("Aucun lot à exporter");
      return;
    }
    const escape = (v) => {
      const s = v === null || v === undefined ? "" : String(v);
      return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headers = ["N° de lot", "Produit", "SKU", "Dépôt", "Emplacement", "Barquette", "Quantité", "Unité", "DLC", "DLC statut", "Date production"];
    const today = new Date(); today.setHours(0,0,0,0);
    const rows = filteredLots.map((l) => {
      const p = productMap[l.product_id];
      const d = distMap[l.distributor_id];
      const s = dlcStatus(l.dlc);
      const statut = !s ? "—" : s.kind === "expired" ? `Dépassée (J${s.days})` : s.kind === "soon" ? `J-${s.days}` : "OK";
      return [
        l.lot_number,
        p?.name || "",
        p?.sku || "",
        d?.name || "",
        l.emplacement_number ?? "",
        l.barquette_index ?? "",
        l.quantity ?? 0,
        p?.unit || "",
        l.dlc || "",
        statut,
        l.production_date || "",
      ].map(escape).join(";");
    });
    const csv = "\uFEFF" + [headers.join(";"), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const stamp = today.toISOString().slice(0, 10);
    const suffix = dlcFilter !== "all" ? `_${dlcFilter}` : "";
    downloadBlob(blob, `stocks_${stamp}${suffix}.csv`);
    toast.success(`${filteredLots.length} lot${filteredLots.length > 1 ? "s" : ""} exporté${filteredLots.length > 1 ? "s" : ""}`);
  };

  return (
    <div className="space-y-6" data-testid="stocks-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Warehouse className="w-4 h-4 text-[#575A57]" />
          <Label className="text-sm text-[#575A57]">Filtrer par dépôt :</Label>
          <Select value={depotFilter} onValueChange={setDepotFilter}>
            <SelectTrigger className="w-[220px]" data-testid="depot-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les dépôts</SelectItem>
              <SelectItem value="type:AUTOMATIQUE">Distributeurs automatiques</SelectItem>
              <SelectItem value="type:CLIENT">Clients</SelectItem>
              <SelectItem value="unassigned">Non assigné</SelectItem>
              {distributors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}{d.code ? ` (${d.code})` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={dlcFilter} onValueChange={setDlcFilter}>
            <SelectTrigger className="w-[180px]" data-testid="dlc-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes DLC</SelectItem>
              <SelectItem value="risk">À risque (dépassée + ≤ 7j)</SelectItem>
              <SelectItem value="expired">DLC dépassée</SelectItem>
              <SelectItem value="soon">DLC ≤ 7 jours</SelectItem>
            </SelectContent>
          </Select>
          <label
            className="flex items-center gap-1.5 text-xs text-[#575A57] cursor-pointer select-none px-2 py-1.5 border border-[#E5E5E0] rounded bg-white hover:bg-[#FAFAF7] transition-colors"
            data-testid="hide-empty-lots-toggle"
            title="Les lots à 0 restent en base pour la traçabilité HACCP mais sont cachés de cette vue."
          >
            <input
              type="checkbox"
              checked={hideEmptyLots}
              onChange={(e) => setHideEmptyLots(e.target.checked)}
              className="w-3.5 h-3.5 accent-[#1F4E3D]"
            />
            Masquer lots vides
          </label>
          <div className="relative">
            <Search className="w-4 h-4 text-[#A3A6A3] absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              type="text"
              placeholder="Rechercher lot, SKU, produit…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 pr-8 w-[260px]"
              data-testid="lot-search-input"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#A3A6A3] hover:text-[#575A57]"
                data-testid="clear-search-btn"
                aria-label="Effacer la recherche"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" /> Exporter CSV
          </Button>
          <Button variant="outline" onClick={() => setScanOpen(true)} data-testid="open-scanner-btn">
            <ScanLine className="w-4 h-4 mr-2" /> Scanner
          </Button>
          <Dialog open={mvOpen} onOpenChange={setMvOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="create-movement-btn"><ArrowDown className="w-4 h-4 mr-2" /> Mouvement</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">Nouveau mouvement</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <Label className="mb-1.5 block">Produit *</Label>
                  <Select value={mvForm.product_id} onValueChange={(v) => setMvForm({ ...mvForm, product_id: v, lot_id: "" })}>
                    <SelectTrigger data-testid="mv-product"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                    <SelectContent>{sortByName(products).map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.sku})</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-1.5 block">Type *</Label>
                    <Select value={mvForm.type} onValueChange={(v) => setMvForm({ ...mvForm, type: v })}>
                      <SelectTrigger data-testid="mv-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="IN">Entrée</SelectItem>
                        <SelectItem value="OUT">Sortie</SelectItem>
                        <SelectItem value="ADJUST">Ajustement</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="mb-1.5 block">Quantité *</Label>
                    <Input type="number" value={mvForm.quantity} onChange={(e) => setMvForm({ ...mvForm, quantity: e.target.value })} data-testid="mv-quantity" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-1.5 block">Dépôt</Label>
                    <Select value={mvForm.distributor_id || "none"} onValueChange={(v) => setMvForm({ ...mvForm, distributor_id: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="mv-depot"><SelectValue placeholder="Aucun" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucun</SelectItem>
                        {distributors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="mb-1.5 block">Lot (optionnel)</Label>
                    <Select value={mvForm.lot_id || "none"} onValueChange={(v) => setMvForm({ ...mvForm, lot_id: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="mv-lot"><SelectValue placeholder="Aucun" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucun</SelectItem>
                        {lots.filter((l) => !mvForm.product_id || l.product_id === mvForm.product_id).map((l) => (
                          <SelectItem key={l.id} value={l.id}>Lot {l.lot_number} — {fmtNum(l.quantity)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="mb-1.5 block">Motif</Label>
                  <Input value={mvForm.reason} onChange={(e) => setMvForm({ ...mvForm, reason: e.target.value })} placeholder="Ex: Réception fournisseur…" data-testid="mv-reason" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setMvOpen(false)}>Annuler</Button>
                <Button onClick={saveMovement} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="save-movement-btn">Enregistrer</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Phase 87ax — Approvisionnement visuel FIFO (vue produit-d'abord) */}
          <VisualTransferDialog
            distributors={distributors}
            products={products}
            lots={lots}
            settings={settings}
            onSuccess={load}
          />


          <Dialog open={trOpen} onOpenChange={(v) => {
            setTrOpen(v);
            if (!v) {
              // Reset complet à la fermeture (Phase 98aa — fix bug mémorisation dépôt source)
              setOutType("TRANSFER");
              setWithdrawReason("");
              setWithdrawNotes("");
              setTrForm({ lot_id: "", quantity: 0, to_distributor_id: "", to_emplacement_number: "", to_barquette_index: "", reason: "" });
            }
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="open-transfer-btn"><ArrowRightLeft className="w-4 h-4 mr-2" /> Sortie de stock</Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2 flex-wrap">
                  {outType === "TRANSFER" ? "Transfert inter-dépôts" : "Mise hors circuit"}
                  {outType === "TRANSFER" && (() => {
                    const srcDist = distMap[lotMap[trForm.lot_id]?.distributor_id];
                    const dstDist = distMap[trForm.to_distributor_id];
                    if (!trForm.lot_id) return null;
                    let label = "";
                    let cls = "";
                    if (!srcDist && dstDist) { label = "Approvisionnement"; cls = "bg-[#E5EFF8] text-[#2D5A87] border-[#C5D8E8]"; }
                    else if (srcDist && dstDist?.type === "CLIENT") { label = "Livraison client"; cls = "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8]"; }
                    else if (srcDist && dstDist) { label = "Inter-distributeurs"; cls = "bg-[#E8F0E5] text-[#5A7A4F] border-[#C8D9C0]"; }
                    return label ? <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${cls}`}>{label}</span> : null;
                  })()}
                </DialogTitle>
                <DialogDescription>
                  {outType === "TRANSFER" ? (
                    <>
                      Déplace des barquettes entre deux dépôts.
                      Couvre 3 cas&nbsp;:
                      <span className="block mt-1 ml-2 text-[11px] leading-relaxed text-[#575A57]">
                        • <strong>Approvisionnement</strong> — du stock central vers un distributeur (entrée chez le distributeur)<br/>
                        • <strong>Inter-distributeurs</strong> — d'un distributeur vers un autre<br/>
                        • <strong>Livraison client</strong> — vers un dépôt de type Client (sans grille)
                      </span>
                      <span className="block mt-1 text-[11px] italic text-[#A3A6A3]">
                        Pour retirer des barquettes (perte, casse, retour), basculez sur <strong>« Mise hors circuit »</strong> →
                      </span>
                    </>
                  ) : (
                    "Retire des barquettes du stock avec un motif HACCP (Perte DLC, Casse, Bloquée, Retour Conthey). L'historique du lot reste tracé pour la traçabilité réglementaire."
                  )}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                {/* Phase 67b — Toggle type de sortie */}
                <div className="grid grid-cols-2 gap-2 p-1 bg-[#F4F4F0] rounded border border-[#E5E5E0]" data-testid="out-type-toggle">
                  <button
                    type="button"
                    onClick={() => setOutType("TRANSFER")}
                    className={`px-3 py-2 rounded text-sm transition-all flex items-center justify-center gap-2 ${
                      outType === "TRANSFER"
                        ? "bg-white text-[#1F4E3D] font-semibold shadow-sm"
                        : "text-[#575A57] hover:text-[#1A1C1A]"
                    }`}
                    data-testid="out-type-transfer"
                  >
                    <ArrowRightLeft className="w-4 h-4" /> Transfert
                  </button>
                  <button
                    type="button"
                    onClick={() => setOutType("WITHDRAW")}
                    className={`px-3 py-2 rounded text-sm transition-all flex items-center justify-center gap-2 ${
                      outType === "WITHDRAW"
                        ? "bg-white text-[#C05C47] font-semibold shadow-sm"
                        : "text-[#575A57] hover:text-[#1A1C1A]"
                    }`}
                    data-testid="out-type-withdraw"
                  >
                    <CalendarX className="w-4 h-4" /> Mise hors circuit
                  </button>
                </div>

                <div>
                  <Label className="mb-1.5 block">Lot {outType === "TRANSFER" ? "à transférer" : "à retirer"} *</Label>
                  <LotCombobox
                    value={trForm.lot_id}
                    onChange={(lotId, lot) => {
                      setTrForm({ ...trForm, lot_id: lotId, quantity: lot?.quantity || 0 });
                    }}
                    lots={lots.filter((l) => l.quantity > 0)}
                    productMap={productMap}
                    depotFromId={depotFromId}
                    fmtNum={fmtNum}
                    placeholder="Tapez un n° de lot, un produit ou un dépôt…"
                    testid="tr-lot"
                  />
                </div>
                {trForm.lot_id && (
                  <div className="bg-[#F9F9F6] border border-[#E5E5E0] rounded p-3 text-xs text-[#575A57]">
                    <div>Depuis : <span className="font-medium text-[#1A1C1A]">{depotFromId(lotMap[trForm.lot_id]?.distributor_id)}</span></div>
                    <div>Disponible : <span className="font-mono">{fmtNum(lotMap[trForm.lot_id]?.quantity, productMap[lotMap[trForm.lot_id]?.product_id]?.unit)}</span></div>
                  </div>
                )}
                <div className={outType === "TRANSFER" ? "grid grid-cols-2 gap-4" : ""}>
                  <div>
                    <Label className="mb-1.5 block">Quantité *</Label>
                    <Input type="number" value={trForm.quantity} onChange={(e) => setTrForm({ ...trForm, quantity: e.target.value })} data-testid="tr-quantity" />
                  </div>
                  {outType === "TRANSFER" && (
                    <div>
                      <Label className="mb-1.5 block">Vers le dépôt *</Label>
                      <Select value={trForm.to_distributor_id} onValueChange={(v) => setTrForm({ ...trForm, to_distributor_id: v, to_emplacement_number: "", to_barquette_index: "" })}>
                        <SelectTrigger data-testid="tr-destination"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                        <SelectContent>
                          {distributors.filter((d) => d.id !== lotMap[trForm.lot_id]?.distributor_id).map((d) => (
                            <SelectItem key={d.id} value={d.id}>{d.name}{d.code ? ` (${d.code})` : ""}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Mode WITHDRAW : sélecteur de motif HACCP */}
                {outType === "WITHDRAW" && (
                  <>
                    <div>
                      <Label className="mb-1.5 block">Motif HACCP *</Label>
                      <Select value={withdrawReason} onValueChange={setWithdrawReason}>
                        <SelectTrigger data-testid="withdraw-reason"><SelectValue placeholder="Choisir un motif…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="DLC_EXPIRED">Perte DLC (date dépassée)</SelectItem>
                          <SelectItem value="BROKEN">Casse / rebut (notes obligatoires)</SelectItem>
                          <SelectItem value="BLOCKED">Bloquée en machine (analyse en cours)</SelectItem>
                          <SelectItem value="RETURN_CONTHEY">Retour à Conthey (réintégration dépôt principal)</SelectItem>
                          <SelectItem value="SAMPLE">Échantillon / dégustation client (essai commercial)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="mb-1.5 block">
                        Notes {withdrawReason === "BROKEN" ? <span className="text-[#C05C47]">*</span> : <span className="text-[#A3A6A3] text-xs">(optionnel)</span>}
                      </Label>
                      <Input
                        value={withdrawNotes}
                        onChange={(e) => setWithdrawNotes(e.target.value)}
                        placeholder={withdrawReason === "BROKEN" ? "Ex: Barquette percée durant transport" : "Détails complémentaires"}
                        data-testid="withdraw-notes"
                      />
                    </div>
                  </>
                )}

                {/* Mode TRANSFER : sélecteur emplacement (uniquement pour dépôt non-CLIENT) */}
                {outType === "TRANSFER" && trForm.to_distributor_id && distMap[trForm.to_distributor_id]?.type === "CLIENT" && (
                  <div className="bg-[#F9F9F6] border border-[#E5E5E0] rounded p-3 text-xs text-[#575A57]">
                    Ce dépôt est de type <span className="font-medium text-[#1A1C1A]">Client</span> : aucun emplacement de grille requis. La quantité transférée sera placée directement dans le dépôt.
                  </div>
                )}
                {outType === "TRANSFER" && trForm.to_distributor_id && distMap[trForm.to_distributor_id]?.type !== "CLIENT" && (
                  <div className="bg-[#F9F9F6] border border-[#E5E5E0] rounded p-3">
                    <Label className="mb-1.5 block">Emplacement destination *</Label>
                    <Select value={trForm.to_emplacement_number || ""} onValueChange={(v) => setTrForm({ ...trForm, to_emplacement_number: v })}>
                      <SelectTrigger data-testid="tr-emplacement"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                      <SelectContent className="max-h-64">
                        {(distMap[trForm.to_distributor_id]?.emplacements || []).length === 0 ? (
                          <div className="px-2 py-3 text-xs text-[#575A57]">Aucun emplacement actif dans ce dépôt</div>
                        ) : (
                          (distMap[trForm.to_distributor_id]?.emplacements || []).map((n) => {
                            const dest = distMap[trForm.to_distributor_id];
                            const capacity = (dest?.emplacement_capacities || {})[String(n)] ?? 4;
                            const presentQty = lots
                              .filter((l) => l.distributor_id === trForm.to_distributor_id && l.emplacement_number === n)
                              .reduce((sum, l) => sum + (l.quantity || 0), 0);
                            const free = Math.max(0, capacity - presentQty);
                            const full = free === 0;
                            const lotsHere = lots.filter((l) => l.distributor_id === trForm.to_distributor_id && l.emplacement_number === n && l.quantity > 0);
                            const sameLot = lotsHere.find((l) => l.lot_number === lotMap[trForm.lot_id]?.lot_number);
                            const label = full
                              ? `Emplacement ${n} (complet ${presentQty}/${capacity})`
                              : `Emplacement ${n} — ${free}/${capacity} libre${free > 1 ? "s" : ""}${sameLot ? " · fusion auto" : lotsHere.length > 0 ? " · autre lot présent" : ""}`;
                            return (
                              <SelectItem key={n} value={String(n)} disabled={full}>
                                {label}
                              </SelectItem>
                            );
                          })
                        )}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-[#575A57] mt-1.5">
                      Si un lot identique est déjà présent sur ce slot, les quantités fusionnent automatiquement.
                    </p>
                  </div>
                )}

                {outType === "TRANSFER" && (
                  <div>
                    <Label className="mb-1.5 block">Motif</Label>
                    <Input value={trForm.reason} onChange={(e) => setTrForm({ ...trForm, reason: e.target.value })} placeholder="Ex: Réapprovisionnement magasin A" data-testid="tr-reason" />
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setTrOpen(false)}>Annuler</Button>
                <Button
                  onClick={saveTransfer}
                  className={outType === "TRANSFER" ? "bg-[#1F4E3D] hover:bg-[#173B2E]" : "bg-[#C05C47] hover:bg-[#A04A38]"}
                  data-testid="save-transfer-btn"
                >
                  {outType === "TRANSFER" ? "Valider le transfert" : "Confirmer la mise hors circuit"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Retrait DLC en masse */}
          <Dialog open={dlcOpen} onOpenChange={(v) => { setDlcOpen(v); if (!v) setDlcPreview(null); }}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="open-dlc-removal-btn" className="text-[#A04E2C] border-[#E8C7BD] hover:bg-[#FBE5E0]">
                <CalendarX className="w-4 h-4 mr-2" /> Retrait DLC
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0">
              <DialogHeader className="px-6 pt-6 pb-3 border-b border-[#E5E5E0]">
                <DialogTitle className="font-heading flex items-center gap-2">
                  <CalendarX className="w-5 h-5 text-[#A04E2C]" /> Retrait des barquettes périmées
                </DialogTitle>
                <DialogDescription>
                  Tous les lots du distributeur sélectionné avec une DLC ≤ date seuil seront retirés en bloc (mouvements OUT créés, quantités passées à 0).
                </DialogDescription>
              </DialogHeader>
              <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-1.5 block">Distributeur *</Label>
                    <Select value={dlcDistId} onValueChange={(v) => { setDlcDistId(v); setDlcPreview(null); }}>
                      <SelectTrigger data-testid="dlc-distributor"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                      <SelectContent>
                        {distributors.filter((d) => d.type !== "CLIENT").map((d) => (
                          <SelectItem key={d.id} value={d.id}>{d.name}{d.code ? ` (${d.code})` : ""}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="mb-1.5 block">DLC seuil (inclus) *</Label>
                    <Input
                      type="date"
                      value={dlcThreshold}
                      onChange={(e) => { setDlcThreshold(e.target.value); setDlcPreview(null); }}
                      data-testid="dlc-threshold"
                    />
                    <p className="text-[11px] text-[#A3A6A3] mt-1">Tout lot avec DLC ≤ cette date sera retiré.</p>
                  </div>
                </div>

                <Button
                  variant="outline"
                  onClick={dlcRunPreview}
                  disabled={!dlcDistId || dlcLoading}
                  data-testid="dlc-preview-btn"
                  className="w-full"
                >
                  {dlcLoading ? "Calcul…" : "🔍 Calculer les lots à retirer"}
                </Button>

                {dlcPreview && (
                  <div className="border border-[#E5E5E0] rounded-lg p-3 bg-[#FAFAF8]" data-testid="dlc-preview-result">
                    {dlcPreview.lots_count === 0 ? (
                      <p className="text-sm text-[#1F4E3D] text-center py-4">✅ Aucun lot à retirer — tous les lots de ce distributeur ont une DLC postérieure à {dlcPreview.threshold_date}.</p>
                    ) : (
                      <>
                        <div className="flex items-center justify-between mb-3 pb-2 border-b border-[#E5E5E0]">
                          <span className="text-sm text-[#3D2914]">
                            <strong className="text-[#A04E2C]">{dlcPreview.lots_count} lot{dlcPreview.lots_count > 1 ? "s" : ""}</strong> ·
                            <strong className="text-[#A04E2C] ml-1">{dlcPreview.total_quantity} barquette{dlcPreview.total_quantity > 1 ? "s" : ""}</strong> à retirer
                          </span>
                          <span className="text-xs text-[#A3A6A3]">{dlcPreview.distributor_name}</span>
                        </div>
                        <div className="space-y-1.5 max-h-64 overflow-y-auto">
                          {dlcPreview.lots_to_remove.map((l) => (
                            <div key={l.id} className="flex items-center gap-2 text-xs py-1.5 px-2 bg-white rounded border border-[#F0F0EB]" data-testid={`dlc-lot-${l.id}`}>
                              <Badge variant="outline" className="bg-[#FBE5E0] border-[#E8C7BD] text-[#A04E2C] font-mono text-[10px]">DLC {l.dlc}</Badge>
                              <span className="font-medium text-[#3D2914] truncate flex-1">{l.product_name || l.product_id}</span>
                              <span className="text-[#A3A6A3] font-mono">{l.lot_number}</span>
                              {l.emplacement_number && <span className="text-[#1F4E3D] font-mono">Slot {l.emplacement_number}</span>}
                              <span className="font-bold text-[#A04E2C]">×{l.quantity}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
              <DialogFooter className="px-6 py-3 border-t border-[#E5E5E0]">
                <Button variant="outline" onClick={() => setDlcOpen(false)}>Annuler</Button>
                <Button
                  onClick={dlcExecute}
                  disabled={!dlcPreview || dlcPreview.lots_count === 0 || dlcLoading}
                  className="bg-[#C05C47] hover:bg-[#A04E2C] text-white"
                  data-testid="dlc-execute-btn"
                >
                  Confirmer le retrait
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Phase 101fb (Feb 2026) — Audit intégrité stock (accès admin) */}
          <Link to="/stocks/audit-integrite">
            <Button
              variant="outline"
              className="border-[#B03B1F] text-[#B03B1F] hover:bg-[#FFF4F1]"
              title="Détecte les écarts entre la quantité en base et la somme des mouvements (fuites silencieuses, BL supprimés)"
              data-testid="stock-audit-btn"
            >
              <FileSearch className="w-4 h-4 mr-2" />
              Audit intégrité
            </Button>
          </Link>

          <Dialog open={lotOpen} onOpenChange={setLotOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="create-lot-btn"><Plus className="w-4 h-4 mr-2" /> Nouveau lot</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">Nouveau lot</DialogTitle></DialogHeader>              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-1.5 block">Produit *</Label>
                    <Select value={lotForm.product_id} onValueChange={(v) => updateLotForm({ product_id: v })}>
                      <SelectTrigger data-testid="lot-product"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                      <SelectContent>{sortByName(products).map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.sku})</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="mb-1.5 block">Dépôt</Label>
                    <Select value={lotForm.distributor_id || "none"} onValueChange={(v) => setLotForm({ ...lotForm, distributor_id: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="lot-depot"><SelectValue placeholder="Aucun" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Non assigné</SelectItem>
                        {distributors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}{d.code ? ` (${d.code})` : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-1.5 block">N° de lot *</Label>
                    <Input value={lotForm.lot_number} onChange={(e) => setLotForm({ ...lotForm, lot_number: e.target.value })} placeholder="Ex: L2026-0142" data-testid="lot-number" />
                  </div>
                  <div>
                    <Label className="mb-1.5 block">Quantité initiale</Label>
                    <Input type="number" value={lotForm.quantity} onChange={(e) => setLotForm({ ...lotForm, quantity: e.target.value })} data-testid="lot-qty" />
                  </div>
                </div>
                {lotForm.distributor_id && (
                  <div>
                    <Label className="mb-1.5 block">Emplacement</Label>
                    <Select value={lotForm.emplacement_number ? String(lotForm.emplacement_number) : "none"} onValueChange={(v) => setLotForm({ ...lotForm, emplacement_number: v === "none" ? "" : v, barquette_index: "" })}>
                      <SelectTrigger data-testid="lot-emplacement"><SelectValue placeholder="Aucun" /></SelectTrigger>
                      <SelectContent className="max-h-64">
                        <SelectItem value="none">Non assigné (stock central)</SelectItem>
                        {(distMap[lotForm.distributor_id]?.emplacements || []).map((n) => {
                          const dest = distMap[lotForm.distributor_id];
                          const capacity = (dest?.emplacement_capacities || {})[String(n)] ?? 4;
                          const presentQty = lots
                            .filter((l) => l.distributor_id === lotForm.distributor_id && l.emplacement_number === n)
                            .reduce((sum, l) => sum + (l.quantity || 0), 0);
                          const free = Math.max(0, capacity - presentQty);
                          return (
                            <SelectItem key={n} value={String(n)} disabled={free === 0}>
                              Slot {n}{free === 0 ? ` (complet ${presentQty}/${capacity})` : ` — ${free}/${capacity} libre${free > 1 ? "s" : ""}`}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-[#575A57] mt-1.5">Le slot peut contenir plusieurs lots simultanément (cas réel : DLC mixtes).</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-1.5 block">Date de production</Label>
                    <Input type="date" value={lotForm.production_date} onChange={(e) => updateLotForm({ production_date: e.target.value })} data-testid="lot-prod" />
                  </div>
                  <div>
                    <Label className="mb-1.5 block">
                      DLC
                      {(() => {
                        const p = products.find((x) => x.id === lotForm.product_id);
                        return p?.shelf_life_days ? (
                          <span className="ml-2 text-xs font-normal text-[#1F4E3D]">
                            · auto (+{p.shelf_life_days} j)
                          </span>
                        ) : null;
                      })()}
                    </Label>
                    <Input type="date" value={lotForm.dlc} onChange={(e) => setLotForm({ ...lotForm, dlc: e.target.value })} data-testid="lot-dlc" />
                  </div>
                </div>
                <div>
                  <Label className="mb-1.5 block">Notes</Label>
                  <Textarea rows={2} value={lotForm.notes} onChange={(e) => setLotForm({ ...lotForm, notes: e.target.value })} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setLotOpen(false)}>Annuler</Button>
                <Button onClick={saveLot} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="save-lot-btn">Créer le lot</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <BarcodeScanner open={scanOpen} onClose={() => setScanOpen(false)} onDetected={handleScan} />

      <Tabs defaultValue="lots">
        <TabsList>
          <TabsTrigger value="lots" data-testid="tab-lots">Lots</TabsTrigger>
          <TabsTrigger value="perpetual" data-testid="tab-perpetual" onClick={loadPerpetual}>
            <GlassWater className="w-3.5 h-3.5 mr-1.5" /> Boissons / Sans DLC
            {perpetualLots.length > 0 && (
              <Badge variant="outline" className="ml-2 text-[10px] bg-[#E8F1EA] border-[#B9D6BD] text-[#1F4E3D]">
                {perpetualLots.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="movements" data-testid="tab-movements">Mouvements</TabsTrigger>
        </TabsList>

        <TabsContent value="lots" className="mt-4">
          {expandableGroupKeys.length > 0 && (
            <div className="flex justify-end mb-2">
              <Button
                variant="outline"
                size="sm"
                onClick={toggleAllGroups}
                data-testid="toggle-all-groups-btn"
              >
                {allExpanded ? (
                  <><ChevronDown className="w-4 h-4 mr-1.5" /> Tout replier</>
                ) : (
                  <><ChevronRight className="w-4 h-4 mr-1.5" /> Tout déplier</>
                )}
              </Button>
            </div>
          )}
          <div className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F4F4F0] hover:bg-[#F4F4F0]">
                  <TableHead className="w-8"></TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">N° de lot</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Produit</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Dépôt</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-right">Quantité</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">DLC</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="lots-table-body">
                {groupedLots.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-[#575A57] py-10">Aucun lot.</TableCell></TableRow>
                ) : groupedLots.map((g) => {
                  const p = productMap[g.product_id];
                  const depotIds = Array.from(g.depot_ids);
                  const depotCell = depotIds.length === 1
                    ? depotBadge(depotIds[0] === "_unassigned" ? null : depotIds[0])
                    : (
                      <Badge variant="outline" className="bg-[#F1EFE9] border-[#E5E5E0] text-[#575A57]">
                        {depotIds.length} dépôts
                      </Badge>
                    );
                  const isExpanded = expandedGroups.has(g.key);
                  const canExpand = g.lots.length > 1;
                  return (
                    <React.Fragment key={g.key}>
                      <TableRow data-testid={`lot-row-${g.lot_number}`} className={canExpand ? "cursor-pointer" : ""} onClick={canExpand ? () => toggleGroup(g.key) : undefined}>
                        <TableCell className="pr-0">
                          {canExpand ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={(e) => { e.stopPropagation(); toggleGroup(g.key); }}
                              data-testid={`expand-lot-${g.lot_number}`}
                            >
                              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            </Button>
                          ) : null}
                        </TableCell>
                        <TableCell className="font-mono">
                          {g.lot_number}
                          {g.lots.length > 1 && (
                            <span className="ml-2 text-[10px] text-[#575A57]">×{g.lots.length}</span>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{p?.name || "—"} <span className="text-xs text-[#575A57] font-mono">({p?.sku})</span></TableCell>
                        <TableCell>{depotCell}</TableCell>
                        <TableCell className="text-right font-mono">{fmtNum(g.quantity, p?.unit)}</TableCell>
                        <TableCell>
                          {g.lots.some((x) => x.frozen_at) ? (
                            <Badge variant="outline" className="bg-[#EAF1F6] text-[#2E6A8E] border-[#BCD3E3] gap-1" title={`Congelé le ${fmtDate(g.lots.find((x) => x.frozen_at).frozen_at)}`} data-testid={`frozen-badge-${g.lot_number}`}>
                              <Snowflake className="w-3 h-3" /> DDM {fmtDate(g.lots.find((x) => x.frozen_at).dluo)}
                            </Badge>
                          ) : renderDlc(g.dlc)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                            <Link to={`/etiquettes?lot=${g.first_lot_id}`}>
                              <Button variant="ghost" size="icon" title="Générer étiquette" data-testid={`label-lot-${g.lot_number}`}>
                                <Tag className="w-4 h-4" />
                              </Button>
                            </Link>
                            {dlcStatus(g.dlc)?.kind === "expired" && g.lots.some((x) => (x.quantity || 0) > 0) && (
                              <Button variant="ghost" size="icon" onClick={() => expireGroup(g)} title="Marquer le lot comme périmé" data-testid={`expire-lot-${g.lot_number}`}>
                                <AlertTriangle className="w-4 h-4 text-[#C05C47]" />
                              </Button>
                            )}
                            {g.lots.length === 1 && !g.lots[0].frozen_at && (g.lots[0].quantity || 0) > 0 && g.dlc && dlcStatus(g.dlc)?.kind !== "expired" && (
                              <FreezeLotDialog lot={g.lots[0]} productName={p?.name} onDone={load} testid={`freeze-lot-${g.lot_number}`} />
                            )}
                            {g.lots.length === 1 ? (
                              <WithdrawLotDialog
                                lot={g.lots[0]}
                                productName={p?.name}
                                distributorName={distMap[g.lots[0].distributor_id]?.name}
                                distributors={distributors}
                                onDone={load}
                                testIdPrefix={`withdraw-group-${g.lot_number}`}
                                trigger={
                                  <Button variant="ghost" size="icon" title="Retirer du stock (DLC/casse/transfert…)" data-testid={`delete-lot-${g.lot_number}`}>
                                    <Trash2 className="w-4 h-4 text-[#C05C47]" />
                                  </Button>
                                }
                              />
                            ) : (
                              <Button
                                variant="ghost"
                                size="icon"
                                disabled
                                title={`Ce lot est éclaté sur ${g.lots.length} dépôt/positions — dépliez la ligne pour retirer une barquette précise.`}
                                data-testid={`delete-lot-${g.lot_number}`}
                                className="opacity-30 cursor-not-allowed"
                              >
                                <Trash2 className="w-4 h-4 text-[#C05C47]" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      {isExpanded && g.lots.map((l) => (
                        <TableRow key={`${g.key}-${l.id}`} className="bg-[#FAFAF7] hover:bg-[#F4F4F0]" data-testid={`barquette-row-${l.id}`}>
                          <TableCell></TableCell>
                          <TableCell className="font-mono text-xs text-[#575A57] pl-8">
                            {l.emplacement_number ? (
                              <Badge variant="outline" className="bg-[#EAF3EE] border-[#C5DBCF] text-[#1F4E3D] font-mono text-[11px]">
                                <MapPin className="w-3 h-3 mr-1" />Slot {l.emplacement_number}
                              </Badge>
                            ) : (
                              <span className="text-[#A3A6A3]">— sans position —</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-[#575A57]">{p?.name || "—"}</TableCell>
                          <TableCell className="text-xs">{depotBadge(l.distributor_id)}</TableCell>
                          <TableCell className="text-right font-mono text-xs">{fmtNum(l.quantity, p?.unit)}</TableCell>
                          <TableCell className="text-xs">{renderDlc(l.dlc)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Link to={`/etiquettes?lot=${l.id}`}>
                                <Button variant="ghost" size="icon" title="Générer étiquette" data-testid={`label-barquette-${l.id}`}>
                                  <Tag className="w-4 h-4" />
                                </Button>
                              </Link>
                              {(l.quantity || 0) > 0 && dlcStatus(l.dlc)?.kind === "expired" && (
                                <Button variant="ghost" size="icon" onClick={() => expireLot(l)} title="Marquer comme périmé (DLC dépassée)" data-testid={`expire-barquette-${l.id}`}>
                                  <AlertTriangle className="w-4 h-4 text-[#C05C47]" />
                                </Button>
                              )}
                              <WithdrawLotDialog
                                lot={l}
                                productName={p?.name}
                                distributorName={distMap[l.distributor_id]?.name}
                                distributors={distributors}
                                onDone={load}
                                testIdPrefix={`withdraw-barquette-${l.id}`}
                                trigger={
                                  <Button variant="ghost" size="icon" title="Retirer du stock (DLC/casse/transfert…)" data-testid={`delete-barquette-${l.id}`}>
                                    <Trash2 className="w-4 h-4 text-[#C05C47]" />
                                  </Button>
                                }
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Phase 87bc — Onglet "Boissons" : lots perpétuels sans DLC pour produits stock_managed=false.
            Affiche la quantité actuelle dans chaque distributeur et propose un bouton "Recharger". */}
        <TabsContent value="perpetual" className="mt-4">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="text-sm text-[#575A57]">
              <strong className="text-[#1A1C1A]">Stock simple sans DLC</strong> — Bouteilles, canettes, snacks de revente.
              <br/>
              <span className="text-xs italic">
                Un lot perpétuel par (produit + distributeur). La sync Nayax décrémente automatiquement à chaque vente.
                Utilisez « Recharger » avant chaque tournée pour ajouter le stock prévu.
              </span>
            </div>
            <Button
              onClick={() => openRefill()}
              className="bg-[#1F4E3D] hover:bg-[#173B2E]"
              data-testid="open-refill-perpetual-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Recharger une boisson
            </Button>
          </div>

          {/* Agrégat rapide par produit (somme machines) */}
          {perpetualLots.length > 0 && (() => {
            const byProduct = new Map();
            for (const l of perpetualLots) {
              const key = l.product_id;
              if (!byProduct.has(key)) {
                byProduct.set(key, {
                  product_id: key,
                  product_name: l.product_name,
                  product_sku: l.product_sku,
                  total: 0,
                  machines: 0,
                });
              }
              const e = byProduct.get(key);
              e.total += l.quantity || 0;
              e.machines += 1;
            }
            const cards = Array.from(byProduct.values()).sort((a, b) => (a.product_name || "").localeCompare(b.product_name || ""));
            return (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="perpetual-totals-grid">
                {cards.map((c) => (
                  <div
                    key={c.product_id}
                    className="bg-white border border-[#E5E5E0] rounded-lg p-3"
                    data-testid={`perpetual-total-${c.product_sku}`}
                  >
                    <div className="text-[10px] uppercase tracking-wider text-[#575A57] font-semibold truncate" title={c.product_name}>
                      {c.product_name}
                    </div>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className="text-2xl font-mono font-bold text-[#1F4E3D]">{c.total}</span>
                      <span className="text-[11px] text-[#A3A6A3]">unités</span>
                    </div>
                    <div className="text-[10px] text-[#575A57] mt-0.5">
                      sur {c.machines} distributeur{c.machines > 1 ? "s" : ""}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}

          <div className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F4F4F0] hover:bg-[#F4F4F0]">
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Produit</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">SKU</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Distributeur</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Slot</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-right">Quantité</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="perpetual-table-body">
                {!perpetualLoaded ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-[#575A57] py-10">Chargement…</TableCell></TableRow>
                ) : perpetualLots.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-[#575A57] py-10">
                      <GlassWater className="w-8 h-8 mx-auto mb-2 text-[#A3A6A3]" />
                      <div className="font-medium">Aucune boisson en stock simple</div>
                      <div className="text-xs italic mt-1">
                        Activez « Stock simple sans DLC » sur la fiche d'un produit (boutique en ligne) pour le voir ici après
                        la 1<sup>re</sup> vente Nayax ou un remplissage manuel.
                      </div>
                    </TableCell>
                  </TableRow>
                ) : perpetualLots.map((l) => {
                  const lowStock = (l.quantity || 0) <= 2;
                  const negative = (l.quantity || 0) < 0;
                  return (
                    <TableRow key={l.id} data-testid={`perpetual-row-${l.id}`}>
                      <TableCell className="font-medium">{l.product_name || "—"}</TableCell>
                      <TableCell className="font-mono text-xs text-[#575A57]">{l.product_sku || "—"}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-[#EAF3EE] border-[#C5DBCF] text-[#1F4E3D]">
                          {l.distributor_name || "—"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">
                        {l.emplacement_number ? (
                          <span className="font-mono text-[#1F4E3D]">Slot {l.emplacement_number}</span>
                        ) : (
                          <span className="text-[#A3A6A3]">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <span
                          className={`font-mono font-bold text-base ${
                            negative ? "text-[#C05C47]" : lowStock ? "text-[#8C6B2F]" : "text-[#1F4E3D]"
                          }`}
                          data-testid={`perpetual-qty-${l.id}`}
                        >
                          {l.quantity ?? 0}
                        </span>
                        {negative && (
                          <div className="text-[10px] text-[#C05C47] mt-0.5">À remplir d'urgence</div>
                        )}
                        {!negative && lowStock && (
                          <div className="text-[10px] text-[#8C6B2F] mt-0.5">Stock bas</div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openRefill(l.product_id, l.distributor_id, l.emplacement_number)}
                          data-testid={`refill-btn-${l.id}`}
                          className="text-[#1F4E3D] border-[#B9D6BD] hover:bg-[#EAF3EE]"
                        >
                          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Recharger
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="movements" className="mt-4">
          <div className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F4F4F0] hover:bg-[#F4F4F0]">
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Date</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Type</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Produit</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Lot</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Dépôt(s)</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-right">Quantité</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Motif</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="movements-table-body">
                {movements.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-[#575A57] py-10">Aucun mouvement.</TableCell></TableRow>
                ) : movements.map((m) => {
                  const p = productMap[m.product_id];
                  const l = m.lot_id ? lotMap[m.lot_id] : null;
                  return (
                    <TableRow key={m.id}>
                      <TableCell className="font-mono text-sm">{fmtDateTime(m.created_at)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="gap-1.5">
                          <MvIcon t={m.type} /> {mvTypeLabel[m.type]}
                        </Badge>
                      </TableCell>
                      <TableCell>{p?.name || "—"}</TableCell>
                      <TableCell className="font-mono text-sm">{l?.lot_number || "—"}</TableCell>
                      <TableCell className="text-sm">
                        {m.type === "TRANSFER" ? (
                          <span className="inline-flex items-center gap-1.5 text-xs">
                            <span className="font-medium">{depotFromId(m.from_distributor_id)}</span>
                            <ArrowRightLeft className="w-3 h-3" />
                            <span className="font-medium">{depotFromId(m.to_distributor_id)}</span>
                          </span>
                        ) : (
                          depotFromId(m.distributor_id)
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono">{fmtNum(m.quantity, p?.unit)}</TableCell>
                      <TableCell className="text-sm text-[#575A57]">{m.reason || "—"}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Phase 87bc — Dialog de remplissage perpétuel (boisson) */}
      <Dialog open={refillOpen} onOpenChange={setRefillOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <GlassWater className="w-5 h-5 text-[#1F4E3D]" /> Recharger une boisson
            </DialogTitle>
            <DialogDescription>
              Incrémente le lot perpétuel (pas de DLC, pas de FIFO). Saisissez la quantité ajoutée
              dans le distributeur après votre tournée. Utilisez une valeur négative pour corriger
              un inventaire (perte, vol, casse).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="mb-1.5 block">Produit *</Label>
              <Select
                value={refillForm.product_id}
                onValueChange={(v) => setRefillForm({ ...refillForm, product_id: v })}
              >
                <SelectTrigger data-testid="refill-product"><SelectValue placeholder="Sélectionner une boisson…" /></SelectTrigger>
                <SelectContent>
                  {(() => {
                    const drinks = products.filter((p) => p.stock_managed === false);
                    if (drinks.length === 0) {
                      return (
                        <div className="px-3 py-2 text-xs text-[#A3A6A3] italic">
                          Aucun produit en stock simple. Cochez « Stock simple sans DLC » sur la fiche
                          d'un produit dans la section « Boutique en ligne ».
                        </div>
                      );
                    }
                    return sortByName(drinks).map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} <span className="text-xs text-[#A3A6A3] font-mono ml-1">({p.sku})</span>
                      </SelectItem>
                    ));
                  })()}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block">Distributeur *</Label>
              <Select
                value={refillForm.distributor_id}
                onValueChange={(v) => setRefillForm({ ...refillForm, distributor_id: v, emplacement_number: "" })}
              >
                <SelectTrigger data-testid="refill-distributor"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                <SelectContent>
                  {distributors.filter((d) => d.type !== "CLIENT").map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}{d.code ? ` (${d.code})` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1.5 block">Quantité à ajouter *</Label>
                <Input
                  type="number"
                  value={refillForm.quantity}
                  onChange={(e) => setRefillForm({ ...refillForm, quantity: e.target.value })}
                  placeholder="Ex: 24"
                  data-testid="refill-quantity"
                />
                <p className="text-[10px] text-[#A3A6A3] italic mt-1">
                  Négatif = correction d'inventaire
                </p>
              </div>
              <div>
                <Label className="mb-1.5 block">Slot (optionnel)</Label>
                <Select
                  value={refillForm.emplacement_number || "none"}
                  onValueChange={(v) => setRefillForm({ ...refillForm, emplacement_number: v === "none" ? "" : v })}
                  disabled={!refillForm.distributor_id}
                >
                  <SelectTrigger data-testid="refill-emplacement"><SelectValue placeholder="Aucun" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value="none">— Aucun —</SelectItem>
                    {(distMap[refillForm.distributor_id]?.emplacements || []).map((n) => (
                      <SelectItem key={n} value={String(n)}>Slot {n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block">Notes (optionnel)</Label>
              <Input
                value={refillForm.notes}
                onChange={(e) => setRefillForm({ ...refillForm, notes: e.target.value })}
                placeholder="Ex: Tournée du 28/06, livraison Migros"
                data-testid="refill-notes"
              />
            </div>

            {/* Aperçu en temps réel : qty actuelle + qty après refill */}
            {refillForm.product_id && refillForm.distributor_id && (() => {
              const existing = perpetualLots.find(
                (l) => l.product_id === refillForm.product_id && l.distributor_id === refillForm.distributor_id
              );
              const currentQty = existing?.quantity ?? 0;
              const newQty = currentQty + (Number(refillForm.quantity) || 0);
              return (
                <div className="bg-[#F9F9F6] border border-[#E5E5E0] rounded p-3 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-[#575A57]">Qty actuelle</span>
                    <span className="font-mono font-bold">{currentQty}</span>
                  </div>
                  <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-[#E5E5E0]">
                    <span className="text-[#1F4E3D] font-semibold">Après remplissage</span>
                    <span className={`font-mono font-bold text-base ${newQty < 0 ? "text-[#C05C47]" : "text-[#1F4E3D]"}`} data-testid="refill-preview-newqty">
                      {newQty}
                    </span>
                  </div>
                  {!existing && (
                    <div className="text-[10px] text-[#8C6B2F] mt-1.5 italic">
                      ⓘ Nouveau lot perpétuel — sera créé automatiquement
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefillOpen(false)} disabled={refillSaving}>
              Annuler
            </Button>
            <Button
              onClick={saveRefill}
              disabled={refillSaving || !refillForm.product_id || !refillForm.distributor_id || !refillForm.quantity}
              className="bg-[#1F4E3D] hover:bg-[#173B2E]"
              data-testid="save-refill-btn"
            >
              {refillSaving ? "Enregistrement…" : "Confirmer le remplissage"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
